import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Card } from "@/components/ui/card";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, X } from "lucide-react";
import { useAudioPlayer } from "@/components/AudioPlayerProvider";
import { getPlayableUrl } from "@/lib/audioUtils";

interface BottomAudioPlayerProps {
  currentBeat: {
    id: string;
    title: string;
    producer: string;
    mp3_url?: string;
    wav_url?: string;
    image_url?: string;
    genre?: string;
    bpm?: number;
    mp3_lease_price?: number;
    wav_lease_price?: number;
    flp_price?: number;
    semi_exclusive_price?: number;
    full_exclusive_price?: number;
  } | null;
  isPlaying: boolean;
  onPlayPause: () => void;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onBeatClick: (beat: any) => void;
}

export const BottomAudioPlayer = ({ 
  currentBeat, 
  isPlaying, 
  onPlayPause,
  onClose,
  onNext,
  onPrevious,
  onBeatClick
}: BottomAudioPlayerProps) => {
  const { 
    currentTime, 
    duration, 
    volume, 
    isMuted, 
    isModalOpen,
    handleVolumeChange,
    toggleMute,
    handleSeek: globalHandleSeek
  } = useAudioPlayer();

  const handleSeek = (value: number[]) => {
    const newTime = (value[0] / 100) * duration;
    globalHandleSeek(newTime);
  };

  const handleNext = () => {
    onNext();
  };

  const handlePrevious = () => {
    onPrevious();
  };


  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!currentBeat) return null;

  return (
    <Card className="fixed bottom-0 left-0 right-0 z-50 rounded-none border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Interactive Top border progress bar */}
      <div 
        className={`absolute top-0 left-0 right-0 h-2 bg-muted/20 group ${!isModalOpen ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
        onClick={!isModalOpen && duration > 0 ? (e) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const percentage = (x / rect.width) * 100;
          const newTime = (percentage / 100) * duration;
          globalHandleSeek(newTime);
        } : undefined}
      >
        <div 
          className={`h-full bg-gradient-primary transition-all duration-300 ease-out ${!isModalOpen ? 'group-hover:bg-gradient-primary/80' : ''}`}
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
      <div 
        className="relative flex items-center gap-2 md:gap-4 p-2 md:p-4 md:cursor-default cursor-pointer"
        onClick={(e) => {
          // On mobile (not md screens), if click is not on controls, open beat modal
          if (window.innerWidth < 768) {
            const target = e.target as HTMLElement;
            const isControlElement = target.closest('button') || target.closest('[data-control]') || target.closest('.progress-bar');
            if (!isControlElement) {
              onBeatClick(currentBeat);
            }
          }
        }}
      >
        {/* Beat Info */}
        <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
          {currentBeat.image_url && (
            <img 
              src={currentBeat.image_url} 
              alt={currentBeat.title}
              className="w-8 h-8 md:w-10 md:h-10 rounded object-cover"
            />
          )}
          <div className="min-w-0 flex-1">
            <p 
              className="font-medium text-xs md:text-sm truncate md:cursor-pointer md:hover:text-primary transition-colors" 
              onClick={(e) => {
                e.stopPropagation();
                onBeatClick(currentBeat);
              }}
            >
              {currentBeat.title}
            </p>
            <p className="text-xs text-muted-foreground truncate hidden sm:block">{currentBeat.producer}</p>
          </div>
        </div>

        {/* Controls - Centered */}
        <div className="flex items-center justify-center gap-1 md:gap-2 absolute left-1/2 transform -translate-x-1/2" data-control>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
            className="rounded-full h-5 w-5 md:h-8 md:w-8 p-0"
          >
            <SkipBack className="h-2.5 w-2.5 md:h-4 md:w-4" />
          </Button>
          
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onPlayPause();
            }}
            size="sm"
            className="rounded-full h-6 w-6 md:h-9 md:w-9 p-0"
          >
            {isPlaying ? (
              <Pause className="h-2.5 w-2.5 md:h-4 md:w-4" />
            ) : (
              <Play className="h-2.5 w-2.5 md:h-4 md:w-4 ml-0.5" />
            )}
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="rounded-full h-5 w-5 md:h-8 md:w-8 p-0"
          >
            <SkipForward className="h-2.5 w-2.5 md:h-4 md:w-4" />
          </Button>
        </div>

        {/* Right Side - Time, Volume, Close */}
        <div className="flex items-center gap-1 md:gap-2 ml-auto">
          {/* Time Display - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground">
            <span className="w-9 text-right">{formatTime(currentTime)}</span>
            <span>/</span>
            <span className="w-9">{formatTime(duration)}</span>
          </div>

          {/* Volume Control - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-1" data-control>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleMute}
              className="rounded-full h-6 w-6 p-0"
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="h-3 w-3" />
              ) : (
                <Volume2 className="h-3 w-3" />
              )}
            </Button>
            <Slider
              value={[volume]}
              onValueChange={(value) => handleVolumeChange(value[0])}
              max={100}
              min={0}
              step={1}
              className="w-16 slider-gradient"
            />
          </div>

          {/* Close Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="rounded-full h-6 w-6 md:h-8 md:w-8 p-0"
            data-control
          >
            <X className="h-3 w-3 md:h-4 md:w-4" />
          </Button>
        </div>
      </div>

    </Card>
  );
};